import { z } from 'zod';
import { defineStepType } from '../contract.js';
import { normalizeControlledText } from '../../hashing/canonical.js';
import { hashControlledText } from '../../hashing/hash.js';
import { fileCategorySchema } from '../../files/categories.js';
/**
 * The person reads specific words and signs their name to them.
 *
 * `document-upload` proves a file arrived. It cannot prove somebody read a particular
 * sentence, which is what a handbook acknowledgment, a harassment policy or a
 * confidentiality agreement is for — and what a surveyor asks about.
 *
 * The evidence is a digest of the exact normalised words, taken from the definition's own
 * sealed content. The browser echoes the digest it rendered; the server compares that to the
 * pinned one and refuses a mismatch. That check is the whole point: without it a person can
 * sign version 3 of a policy while the record says version 4, and nothing would ever notice.
 */
/**
 * Normalised INSIDE the schema, so the stored value is already normalised.
 *
 * Normalising only at hash time is the latent bug canonical.ts warns about: the stored text
 * then no longer re-hashes to its own recorded digest, and re-verifying a signed
 * acknowledgment fails years later with nobody able to reconstruct why. `.transform` is legal
 * here by design — `SchemaOf<T>` leaves the input `unknown` precisely so configs may
 * default and transform.
 */
const controlledText = z.string().min(1).transform(normalizeControlledText);
/**
 * A SECOND SIGNATURE ON THE SAME DOCUMENT, by somebody other than the person.
 *
 * Hepatitis B's declination is the case that needs it: the accepted closure for
 * `hepb-offer-and-response` is *"signed and dated IC 5.12B.8 waiver countersigned by the
 * supervisor"*, and the 06b design (3.7) says the candidate signs and then *"a supervisor also
 * signs"*. `visit-signature-policy-attestation` and the orientation checklist have the same shape.
 *
 * MODELLED AS A SECOND PHASE ON ONE INSTANCE, not as a second step and not as a signed approval.
 * The evidence a surveyor asks for is ONE countersigned document, so the two signatures have to end
 * up on one artifact with one text digest between them. Two steps would produce two records that
 * nothing ties together; folding it into approval would conflate "a supervisor signed this waiver"
 * with "the office accepted this submission", which are different claims by different people for
 * different reasons.
 *
 * Absent by default. A definition that says nothing about countersigning behaves exactly as it does
 * today — `undefined`, not a flag defaulting to false, so an existing pin parses unchanged.
 */
export const countersignatureSchema = z
    .object({
    /** Shown to the countersigner, e.g. 'Supervisor'. Also what the record calls them. */
    roleLabel: z.string().min(1).max(80),
    signatureLabel: z.string().min(1).max(200).default('I have witnessed and countersign the above'),
})
    .strict();
export const acknowledgmentConfigSchema = z.object({
    heading: z.string().min(1),
    controlledText,
    signatureLabel: z.string().min(1).default('I have read and agree to the above'),
    requirementKey: z.string().min(1).nullable().default(null),
    /** Optional for existing pins; a configured target requires a server-produced PDF. */
    target: fileCategorySchema.optional(),
    /** Absent means no countersignature, which is every definition that exists today. */
    countersignature: countersignatureSchema.optional(),
});
export const acknowledgmentDraftSchema = z.object({
    typedName: z.string().min(1).max(200),
    agreedAt: z.string().datetime(),
    /** A staleness check against the pinned words, never a client-controlled authority. */
    attestedTextHash: z.string().regex(/^sha256:[0-9a-f]{64}$/),
});
/**
 * The countersigner's half. Carries its OWN `attestedTextHash` on purpose.
 *
 * The person and the supervisor sign at different times, and the words could in principle be
 * republished between the two. A single shared hash would let the supervisor countersign version 4
 * of a waiver the employee signed at version 3 and leave one digest on the record saying they
 * agreed to the same thing. Each signature attests to what THAT signer was shown, and
 * `checkAcknowledgmentText` is run against both.
 */
export const countersignatureRecordSchema = z
    .object({
    typedName: z.string().min(1).max(200),
    agreedAt: z.string().datetime(),
    attestedTextHash: z.string().regex(/^sha256:[0-9a-f]{64}$/),
})
    .strict();
export const acknowledgmentSubmissionSchema = acknowledgmentDraftSchema.extend({
    uploadId: z.string().min(1).optional(),
    fileName: z.string().min(1).optional(),
    /** Present only once the countersigner has signed; absent while the step awaits them. */
    countersignedBy: countersignatureRecordSchema.optional(),
});
/** Shared by pre-staging validation and the final submission check. */
export async function checkAcknowledgmentText(config, submission, pinnedContent) {
    const expected = parsePinnedTextHash(pinnedContent) ?? (await hashControlledText(config.controlledText));
    return submission.attestedTextHash === expected ? { ok: true } : {
        ok: false,
        message: 'This was updated while you had it open. Please reload the page, read the current version, and sign that one.',
    };
}
const module = defineStepType({
    type: 'acknowledgment',
    /** The whole evidentiary claim is that THIS person agreed to these words. */
    submittedBy: 'person',
    label: 'Read and sign',
    description: 'Presents controlled wording and records that a named person agreed to it.',
    configSchema: acknowledgmentConfigSchema,
    submissionSchema: acknowledgmentSubmissionSchema,
    readyToSubmit: ({ draft }) => acknowledgmentDraftSchema.safeParse(draft).success,
    /**
     * Deliberately no `expiresAt`. All ten target rows are `kind: 'document'`, and
     * `evaluateByKind` sends those to `fromSteps`, which ignores dates entirely — so an
     * expiry here would be inert, and a field that looks like it works but does nothing is
     * worse than its absence. Annual re-attestation is Phase 3/4 work.
     */
    /** The countersigned copy, where the type collects one; absent until then. */
    uploads: ({ submission }) => (submission.uploadId === undefined ? [] : [submission.uploadId]),
    onApprove: ({ instance, config, submission }) => [
        ...(config.target !== undefined && submission.uploadId !== undefined ? [{
                kind: 'store-file',
                instanceId: instance.id,
                uploadId: submission.uploadId,
                category: config.target,
                source: 'pdf-artifact',
                plan: null,
            }] : []),
        ...(config.requirementKey !== null
            ? [
                {
                    kind: 'mint-credential',
                    personId: instance.personId,
                    requirementKey: config.requirementKey,
                    identifier: null,
                    issuedAt: null,
                    expiresAt: null,
                },
            ]
            : []),
        { kind: 'recompute-rollup', personId: instance.personId },
    ],
    checkSubmission: async ({ config, submission, pinnedContent }) => {
        if (config.target !== undefined && (submission.uploadId === undefined || submission.fileName === undefined)) {
            return { ok: false, message: 'The signed acknowledgment PDF is missing. Please submit the form again.' };
        }
        // A countersignature on a definition that never asked for one is not a harmless extra: it
        // would ride along into the stored submission and the PDF as if somebody's authority applied.
        if (config.countersignature === undefined && submission.countersignedBy !== undefined) {
            return { ok: false, message: 'This form does not take a second signature.' };
        }
        const person = await checkAcknowledgmentText(config, submission, pinnedContent);
        if (!person.ok)
            return person;
        // Checked against the SAME pinned words, separately, because the two signatures happen at
        // different times and each attests only to what its own signer was shown.
        return submission.countersignedBy === undefined
            ? { ok: true }
            : checkAcknowledgmentText(config, submission.countersignedBy, pinnedContent);
    },
});
export const acknowledgment = {
    ...module,
    runtime: {
        ...module.runtime,
        approve: (instance) => {
            const config = acknowledgmentConfigSchema.safeParse(instance.config);
            const submission = acknowledgmentSubmissionSchema.safeParse(instance.submission);
            // Old, untargeted acknowledgments remain approvable. New filed acknowledgments cannot
            // mint completion if their stored submission has lost the PDF produced by the server.
            if (config.success && config.data.target !== undefined && submission.success &&
                (submission.data.uploadId === undefined || submission.data.fileName === undefined)) {
                return { ok: false, issues: ['The signed acknowledgment PDF is missing. Send the form back for resubmission.'] };
            }
            /**
             * THE WHOLE POINT OF DECLARING A COUNTERSIGNATURE. Approving without it would mint the
             * credential for `hepb-offer-and-response` off a waiver the supervisor never signed — the
             * exact evidence CHAP asks to see, recorded as present when it is not. Refused here rather
             * than only in the UI, because approval is reachable by API.
             */
            if (config.success && config.data.countersignature !== undefined && submission.success &&
                submission.data.countersignedBy === undefined) {
                return {
                    ok: false,
                    issues: [`This form is not complete until the ${config.data.countersignature.roleLabel.toLowerCase()} countersigns it.`],
                };
            }
            return module.runtime.approve(instance);
        },
    },
};
/**
 * Read the sealed digest out of the pinned content.
 *
 * `pinnedDefinitionSchema` has no `controlledTextHash` field of its own — the digest lives
 * inside the sealed `canonicalContent`, which is where it belongs, since that is the thing
 * whose integrity is guaranteed. Parsing it out avoids widening the pin shape and keeps
 * every instance already in the database readable.
 *
 * Returns null when the content carries no digest, and the caller falls back to hashing the
 * pinned config's own text. It never throws: a malformed pin must not make a step
 * unsubmittable, and the comparison below still refuses anything that does not match.
 */
function parsePinnedTextHash(pinnedContent) {
    try {
        const parsed = JSON.parse(pinnedContent);
        if (typeof parsed !== 'object' || parsed === null)
            return null;
        const hash = parsed.controlledTextHash;
        return typeof hash === 'string' ? hash : null;
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=acknowledgment.js.map